import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anonymous
  const [wishlist, setWishlist] = useState([]);

  const applyUser = (data) => {
    setUser(data);
    setWishlist(data?.wishlist || []);
  };

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data);
    } catch {
      applyUser(null);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    applyUser(data);
    return data;
  };

  const loginWithGoogleSession = async (session_id) => {
    const { data } = await api.post("/auth/google", { session_id });
    applyUser(data);
    return data;
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    applyUser(null);
  };

  const toggleWishlist = async (productId) => {
    const isIn = wishlist.includes(productId);
    setWishlist((prev) => (isIn ? prev.filter((id) => id !== productId) : [...prev, productId]));
    try {
      if (isIn) await api.delete(`/wishlist/${productId}`);
      else await api.post(`/wishlist/${productId}`);
    } catch {
      setWishlist((prev) => (isIn ? [...prev, productId] : prev.filter((id) => id !== productId)));
    }
  };

  const value = { user, wishlist, login, register, logout, checkAuth, startGoogleLogin, loginWithGoogleSession, toggleWishlist };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
